'use client';

import * as React from 'react';

/**
 * Props for configuring the useCopyToClipboard hook.
 * @property {number} [timeout=2000] Duration in milliseconds for which the "copied" state remains true.
 */
export interface UseCopyToClipboardProps {
  timeout?: number;
}

/**
 * Custom React hook that provides clipboard copy functionality.
 *
 * @param {UseCopyToClipboardProps} props - Hook configuration options.
 * @returns {{
 *   isCopied: boolean,
 *   copyToClipboard: (value: string) => Promise<boolean>
 * }} An object containing the current copied state and a function to copy text.
 *
 * @throws {Error} If the Clipboard API is not available, no value is provided, or the copy fails.
 */
export function useCopyToClipboard({ timeout = 2000 }: UseCopyToClipboardProps) {
  const [isCopied, setIsCopied] = React.useState<boolean>(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  console.log('useCopyToClipboard - timeout:', timeout);

  /**
   * Asynchronously copies the provided text to the clipboard.
   *
   * @param {string} value - The text to copy.
   * @returns {Promise<boolean>} Resolves to true if the copy was successful.
   * @throws {Error} Throws an error if the Clipboard API is unavailable, no value is provided, or the copy fails.
   */
  const copyToClipboard = async (value: string): Promise<boolean> => {
    if (typeof window === 'undefined' || !navigator.clipboard?.writeText) {
      const errMsg = 'Clipboard API not available';
      console.error(`copyToClipboard - ${errMsg}`);
      throw new Error(errMsg);
    }

    if (!value) {
      const errMsg = 'No value provided to copy';
      console.error(`copyToClipboard - ${errMsg}`);
      throw new Error(errMsg);
    }

    try {
      console.log('copyToClipboard - Copying text:', value);
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      console.log('copyToClipboard - Text copied:', value);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, timeout);

      return true;
    } 
    catch (error) {
      console.error('copyToClipboard - Failed to copy text:', error);
      throw new Error(
        `copyToClipboard failed: ${
          error instanceof Error
            ? error.message + (error.stack ? ` | Stack: ${error.stack}` : '')
            : JSON.stringify(error)
        }`
      );
    }
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { isCopied, copyToClipboard };
}